import Keycloak from 'keycloak-js';
import { auth } from './config';

declare global {
  interface Window {
    __VYEDOCS_ACCESS_TOKEN__?: string;
    __VYEDOCS_CLINIC__?: { name?: string; logo?: string };
    __VYEDOCS_USER__?: {
      name: string;
      username: string;
      email?: string;
      phone?: string;
      registroAcess?: string;
    };
  }
}

let keycloak: Keycloak | undefined;

export async function initAuth(): Promise<boolean> {
  try {
    keycloak = new Keycloak({
      url: auth.url,
      realm: auth.realm,
      clientId: auth.clientId,
    });
    // No check-sso / silent iframe: Keycloak envía CSP frame-ancestors 'self'.
    const ok = await keycloak.init({
      pkceMethod: 'S256',
      checkLoginIframe: false,
      scope: 'openid organization',
    });
    publish();
    keycloak.onAuthSuccess = publish;
    keycloak.onAuthRefreshSuccess = publish;
    keycloak.onTokenExpired = () => {
      void keycloak?.updateToken(30).then((refreshed) => {
        if (refreshed) publish();
      });
    };
    return !!ok;
  } catch (err) {
    console.warn('Keycloak no disponible; el sitio sigue (landpage).', err);
    window.__VYEDOCS_ACCESS_TOKEN__ = undefined;
    return false;
  }
}

function clinicRedirectUri(path = '/app'): string {
  return `${window.location.origin}${path}`;
}

export async function login(redirectPath = `${window.location.pathname}${window.location.search}`): Promise<void> {
  if (!keycloak) await initAuth();
  const path = redirectPath.startsWith('/app') ? redirectPath : '/app';
  await keycloak?.login({ redirectUri: clinicRedirectUri(path) });
}

/** Historia clínica: si no hay sesión, redirect a Keycloak. No monta el producto. */
export async function requireClinicSession(): Promise<boolean> {
  if (!keycloak) await initAuth();
  if (isAuthenticated()) return true;
  await login(window.location.pathname.startsWith('/app') ? window.location.pathname : '/app');
  return false;
}

export async function logout(): Promise<void> {
  window.__VYEDOCS_ACCESS_TOKEN__ = undefined;
  window.__VYEDOCS_USER__ = undefined;
  try {
    sessionStorage.removeItem('vyedocs_access_token');
  } catch {
    /* modo privado */
  }
  await keycloak?.logout({ redirectUri: window.location.origin + '/' });
}

export function username(): string {
  return keycloak?.tokenParsed?.preferred_username || '';
}

export function sessionUser(): {
  name: string;
  username: string;
  email?: string;
  phone?: string;
  registroAcess?: string;
} {
  const parsed = (keycloak?.tokenParsed || {}) as Record<string, unknown>;
  const given = [parsed['given_name'], parsed['family_name']].filter((part) => typeof part === 'string' && part).join(' ');
  const name = String(parsed['name'] || given || parsed['preferred_username'] || '');
  const phone = String(parsed['phone_number'] || parsed['phone'] || parsed['telefono'] || '');
  const registroAcess = String(parsed['registro_acess'] || parsed['registroAcess'] || parsed['acess'] || '');
  return {
    name,
    username: String(parsed['preferred_username'] || ''),
    email: typeof parsed['email'] === 'string' ? parsed['email'] : undefined,
    phone: phone || undefined,
    registroAcess: registroAcess || undefined,
  };
}

export function isAuthenticated(): boolean {
  return !!window.__VYEDOCS_ACCESS_TOKEN__;
}

function publish(): void {
  window.__VYEDOCS_ACCESS_TOKEN__ = keycloak?.token;
  window.__VYEDOCS_USER__ = keycloak?.token ? sessionUser() : undefined;
  try {
    if (keycloak?.token) sessionStorage.setItem('vyedocs_access_token', keycloak.token);
    else sessionStorage.removeItem('vyedocs_access_token');
  } catch {
    /* modo privado */
  }
}
